import React, { useCallback, useMemo, useState } from 'react'
import { gql } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import { Stack } from 'react-bootstrap'
import { useMutation } from '@ztechapps/common.hook'
import { formatMoney } from '@ztechapps/common.utils'
import { Alert, Box, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { TextInput } from '@ztechapps/webui.input'
import type { BuildProductResult } from '@ztechapps/common.contract'
import { BuildProductStatusEnum } from '@ztechapps/common.contract'
import type { ProductDraft, ProductProposal } from '../../CreateProductProposalCard'
import { CreateProductProposalGroup } from '../../CreateProductProposalGroup'
import { useCreateProductCommit } from '../../hooks/useCreateProductCommit'
import { useAiChatContext } from '../../ai-chat/Context'
import { EntityCreatedMessage } from '../EntityCreatedMessage'

const CREATE_PURCHASE_ORDER_MUTATION = gql`
  mutation createPurchaseOrder($input: JSON!) {
    createPurchaseOrder(input: $input) {
      _id
      code
    }
  }
`

export type PurchaseOrderDraftItem = {
  productId?: string
  inlineProposalRef?: string
  quantity: number
  unitPrice?: number
  vendorCode?: string
  description?: string
}

export type PurchaseOrderDraft = {
  vendor: { _id: string; name?: string }
  items: PurchaseOrderDraftItem[]
  shippingAddress?: Record<string, string>
  billingAddress?: Record<string, string>
  referenceNumber?: string
  notes?: string
  tags?: string[]
}

export type PurchaseOrderDraftCardProps = {
  purchaseOrderDraft: PurchaseOrderDraft
  inlineProductProposals: ProductProposal[]
}

type ProposalCommitResult = {
  productId: string
  code: string
}

/**
 * Renders a PO draft (`kind: 'purchase-order-draft'`) plus its nested
 * inline product proposals. Submit is gated until every proposal is either
 * committed (resolved to a real `productId`) or replaced with an existing
 * product via the inline swap input.
 *
 * On submit, the gate-pass map (`ref → productId`) is used to swap every
 * line item's `inlineProposalRef` for the resolved `productId` before
 * the PO mutation fires — keeping the existing `createPurchaseOrder`
 * mutation contract unchanged.
 */
export const PurchaseOrderDraftCard = ({ purchaseOrderDraft, inlineProductProposals }: PurchaseOrderDraftCardProps) => {
  const { purchaseOrderUrl } = useAiChatContext()
  const { commit: defaultCommit } = useCreateProductCommit()
  const [createPurchaseOrder, , { loading: poLoading, error: poError }] = useMutation<{
    createPurchaseOrder: { _id: string; code: string }
  }>({ query: CREATE_PURCHASE_ORDER_MUTATION })

  // ref → { productId, code } for every proposal that's been satisfied
  // (either committed via the engine, OR swapped out for an existing
  // product via the inline replacement input — both routes feed the
  // same map).
  const [resolutionMap, setResolutionMap] = useState<Record<string, ProposalCommitResult>>({})
  const [committedPo, setCommittedPo] = useState<{ _id: string; code: string } | undefined>()
  const [submitError, setSubmitError] = useState<string | undefined>()

  const handleProposalCommit = useCallback(
    async (proposal: ProductProposal, patch: Partial<ProductDraft>): Promise<BuildProductResult> => {
      const result = await defaultCommit(proposal, patch)
      if (result.status === BuildProductStatusEnum.Created && proposal.ref) {
        setResolutionMap((prev) => ({
          ...prev,
          [proposal.ref ?? '']: { productId: result._id, code: result.code },
        }))
      }
      return result
    },
    [defaultCommit]
  )

  const handleReplaceWithExisting = useCallback((proposalRef: string, replacement: { _id: string; code: string }) => {
    setResolutionMap((prev) => ({
      ...prev,
      [proposalRef]: { productId: replacement._id, code: replacement.code },
    }))
  }, [])

  const allRefsSatisfied = useMemo(() => {
    const refs = inlineProductProposals.map((p, i) => p.ref ?? `proposal-${i}`)
    return refs.every((ref) => resolutionMap[ref])
  }, [inlineProductProposals, resolutionMap])

  const handleCreatePO = useCallback(async () => {
    setSubmitError(undefined)
    const resolvedItems = purchaseOrderDraft.items.map((item) => {
      if (item.productId) return stripRef(item)
      const ref = item.inlineProposalRef
      const resolved = ref ? resolutionMap[ref] : undefined
      if (!resolved) {
        throw new Error(`Line item with proposal ref "${ref}" has not been satisfied yet.`)
      }
      return stripRef({ ...item, productId: resolved.productId })
    })

    const input = {
      vendor: { _id: purchaseOrderDraft.vendor._id },
      items: resolvedItems,
      shippingAddress: purchaseOrderDraft.shippingAddress,
      billingAddress: purchaseOrderDraft.billingAddress,
      referenceNumber: purchaseOrderDraft.referenceNumber,
      notes: purchaseOrderDraft.notes,
      tags: purchaseOrderDraft.tags,
    }

    try {
      const result = await createPurchaseOrder({ input })
      const po = result?.data?.createPurchaseOrder
      if (po?._id) {
        setCommittedPo({ _id: po._id, code: po.code })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setSubmitError(message)
    }
  }, [createPurchaseOrder, purchaseOrderDraft, resolutionMap])

  if (committedPo) {
    return (
      <EntityCreatedMessage
        label="PO Created:"
        code={committedPo.code}
        ariaLabel={`View purchase order ${committedPo.code}`}
        url={purchaseOrderUrl?.({ _id: committedPo._id, code: committedPo.code } as any)}
      />
    )
  }

  const proposalCount = inlineProductProposals.length

  return (
    <Box className="bg-body">
      <Stack gap={3}>
        <Stack gap={1}>
          <span className="fw-semibold text-body">PO Draft</span>
          <div className="small text-body-secondary">
            Vendor: <span className="fw-semibold">{purchaseOrderDraft.vendor.name ?? 'Unknown'}</span>
          </div>
          <ShowIf condition={!!purchaseOrderDraft.referenceNumber}>
            <div className="small text-body-secondary">Ref #: {purchaseOrderDraft.referenceNumber}</div>
          </ShowIf>
        </Stack>

        <ShowIf condition={!isEmpty(purchaseOrderDraft.items)}>
          <Stack gap={1}>
            {purchaseOrderDraft.items.map((item, i) => (
              <LineItemRow
                key={`item-${i}`}
                item={item}
                resolvedProductCode={item.inlineProposalRef ? resolutionMap[item.inlineProposalRef]?.code : undefined}
              />
            ))}
          </Stack>
        </ShowIf>

        <ShowIf condition={proposalCount > 0}>
          <Stack gap={2}>
            <span className="fw-semibold text-body small">
              {proposalCount === 1 ? '1 product to create' : `${proposalCount} products to create`}
            </span>
            <CreateProductProposalGroup
              proposals={inlineProductProposals}
              onCommit={handleProposalCommit}
              hideHeader
              onCardSettled={({ proposal, result }) => {
                if (result?.status === BuildProductStatusEnum.Created && proposal.ref && result._id) {
                  setResolutionMap((prev) => ({
                    ...prev,
                    [proposal.ref ?? '']: { productId: result._id, code: result.code },
                  }))
                }
              }}
            />
            <ProposalReplaceList
              proposals={inlineProductProposals}
              resolutionMap={resolutionMap}
              onReplace={handleReplaceWithExisting}
            />
          </Stack>
        </ShowIf>

        <ShowIf condition={!!submitError || !!poError}>
          <Alert type="error">
            <span className="text-body small">
              {submitError ?? (poError instanceof Error ? poError.message : 'Failed to create PO.')}
            </span>
          </Alert>
        </ShowIf>

        <HorizontalStack gap={2} className="justify-content-end">
          <BusyButton
            variant="primary"
            text="Create PO"
            isBusy={poLoading}
            busyText="Creating PO…"
            disabled={!allRefsSatisfied}
            onClick={handleCreatePO}
          />
        </HorizontalStack>
      </Stack>
    </Box>
  )
}

const stripRef = (item: PurchaseOrderDraftItem): Omit<PurchaseOrderDraftItem, 'inlineProposalRef'> => {
  const { inlineProposalRef: _ref, ...rest } = item
  return rest
}

type LineItemRowProps = {
  item: PurchaseOrderDraftItem
  resolvedProductCode?: string
}
const LineItemRow = ({ item, resolvedProductCode }: LineItemRowProps) => {
  const isPending = !!item.inlineProposalRef && !resolvedProductCode
  return (
    <HorizontalStack
      gap={2}
      className="justify-content-between align-items-center small bg-body-secondary rounded px-2 py-1"
    >
      <span className="text-body text-truncate">{item.description ?? item.vendorCode ?? item.productId ?? 'Item'}</span>
      <HorizontalStack gap={2} className="align-items-center">
        <span className="text-body-secondary">Qty {item.quantity}</span>
        <ShowIf condition={typeof item.unitPrice === 'number'}>
          <span className="text-body-secondary">{formatMoney(item.unitPrice)}</span>
        </ShowIf>
        <ShowIf condition={!!resolvedProductCode}>
          <span className="badge bg-success-subtle text-success-emphasis">{resolvedProductCode}</span>
        </ShowIf>
        <ShowIf condition={isPending}>
          <span className="badge bg-warning-subtle text-warning-emphasis">pending creation</span>
        </ShowIf>
      </HorizontalStack>
    </HorizontalStack>
  )
}

type ProposalReplaceListProps = {
  proposals: ProductProposal[]
  resolutionMap: Record<string, ProposalCommitResult>
  onReplace: (proposalRef: string, replacement: { _id: string; code: string }) => void
}
const ProposalReplaceList = ({ proposals, resolutionMap, onReplace }: ProposalReplaceListProps) => {
  // For each proposal that has NOT been satisfied yet, surface a manual
  // "use an existing product instead" entry. The replacement is a free-form
  // `_id + code` text pair to avoid a hard dependency on the product webui
  // module's ProductSelect — the admin pastes the values they want.
  return (
    <Stack gap={1}>
      {proposals.map((proposal, i) => {
        const ref = proposal.ref ?? `proposal-${i}`
        if (resolutionMap[ref]) return null
        return <ReplaceRow key={ref} proposalRef={ref} proposalCode={proposal.draft.code} onReplace={onReplace} />
      })}
    </Stack>
  )
}

type ReplaceRowProps = {
  proposalRef: string
  proposalCode: string
  onReplace: (proposalRef: string, replacement: { _id: string; code: string }) => void
}
const ReplaceRow = ({ proposalRef, proposalCode, onReplace }: ReplaceRowProps) => {
  const [productId, setProductId] = useState('')
  const [productCode, setProductCode] = useState('')

  const handleReplace = () => {
    if (isEmpty(productId) || isEmpty(productCode)) return
    onReplace(proposalRef, { _id: productId, code: productCode })
  }

  return (
    <HorizontalStack gap={2} className="align-items-center small">
      <span className="text-body-secondary">Or use existing for {proposalCode}:</span>
      <TextInput
        placeholder="Product _id"
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
        autoFocus={false}
      />
      <TextInput
        placeholder="Code"
        value={productCode}
        onChange={(e) => setProductCode(e.target.value)}
        autoFocus={false}
      />
      <BusyButton
        variant="outline-secondary"
        bsSize="sm"
        text="Use existing"
        disabled={isEmpty(productId) || isEmpty(productCode)}
        onClick={handleReplace}
      />
    </HorizontalStack>
  )
}
